import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Spinner, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { useAuthContext } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faPrint, faSave } from '@fortawesome/free-solid-svg-icons';
import './Receituario.css'; // CSS para impressão

const ReceituarioTab = ({ consultaId, paciente }) => {
    const { loggedInUser } = useAuthContext(); // Para obter o nome do médico
    
    const initialMedicamentoState = {
        id: Date.now(), // Chave temporária
        nome_medicamento: '',
        dosagem: '',
        posologia: '',
        via_administracao: 'Via Oral',
        quantidade: '',
        uso_continuo: false,
    };

    const [medicamentos, setMedicamentos] = useState([initialMedicamentoState]);
    const [recomendacoes, setRecomendacoes] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isFormDirty, setIsFormDirty] = useState(false);

    // Busca o receituário existente
    const fetchReceituario = useCallback(async () => {
        if (!consultaId) return;
        setIsLoading(true);
        try {
            // BACKEND: Endpoint para buscar o receituário de uma consulta
            const response = await axios.get(`/api/consultas/${consultaId}/receituario`);
            if (response.data && response.data.receituario) {
                const { medicamentos: loadedMeds, recomendacoes: loadedRecomendacoes } = JSON.parse(response.data.receituario.dados_receituario);
                setMedicamentos(loadedMeds.length > 0 ? loadedMeds.map(m => ({...m, id: m.id || Date.now()})) : [initialMedicamentoState]);
                setRecomendacoes(loadedRecomendacoes || '');
            } else {
                setMedicamentos([initialMedicamentoState]);
                setRecomendacoes('');
            }
        } catch (err) {
            console.log("Nenhum receituário anterior encontrado para esta consulta.");
            setMedicamentos([initialMedicamentoState]);
            setRecomendacoes('');
        } finally {
            setIsLoading(false);
            setIsFormDirty(false);
        }
    }, [consultaId]);

    useEffect(() => {
        fetchReceituario();
    }, [fetchReceituario]);

    // Handlers para medicamentos
    const handleMedicamentoChange = (index, field, value) => {
        const newMedicamentos = [...medicamentos];
        newMedicamentos[index][field] = value;
        setMedicamentos(newMedicamentos);
        setIsFormDirty(true);
    };

    const handleAddMedicamento = () => {
        setMedicamentos([...medicamentos, { ...initialMedicamentoState, id: Date.now() }]);
        setIsFormDirty(true);
    };

    const handleRemoveMedicamento = (index) => {
        if (medicamentos.length <= 1) {
            // Limpa o primeiro item em vez de remover
            setMedicamentos([{...initialMedicamentoState, id: Date.now()}]);
        } else {
            const newMedicamentos = [...medicamentos];
            newMedicamentos.splice(index, 1);
            setMedicamentos(newMedicamentos);
        }
        setIsFormDirty(true);
    };

    // Salvar receituário
    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const dados_receituario = JSON.stringify({ medicamentos, recomendacoes });
            // BACKEND: Endpoint para salvar/atualizar o receituário
            await axios.post(`/api/consultas/${consultaId}/receituario`, { dados_receituario });
            setIsFormDirty(false);
            alert('Receituário salvo com sucesso!');
        } catch (err) {
            setError("Erro ao salvar o receituário.");
        } finally {
            setIsSaving(false);
        }
    };

    // Imprimir
    const handlePrint = () => {
        window.print();
    };

    if (isLoading) {
        return <div className="text-center my-5"><Spinner animation="border" /></div>;
    }

    return (
        <>
            <Card.Header className="d-flex justify-content-between align-items-center no-print">
                <span>Gerador de Receituário</span>
                <div>
                    <Button variant="outline-secondary" onClick={handlePrint} className="me-2">
                        <FontAwesomeIcon icon={faPrint} className="me-2" /> Imprimir
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving || !isFormDirty}>
                        {isSaving ? <Spinner as="span" size="sm" /> : <FontAwesomeIcon icon={faSave} />} Salvar
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <div id="printable-receituario">
                    <header className="receituario-header">
                        <h4>{loggedInUser?.nome || 'Nome do Médico'}</h4>
                        <p>{/* Adicione aqui o CRM ou outra info do médico */}</p>
                        <hr />
                    </header>

                    <div className="receituario-paciente-info">
                        <p><strong>Paciente:</strong> {paciente?.nome || 'Nome do Paciente'}</p>
                        <p><strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}</p>
                    </div>

                    <div className="receituario-content">
                        <h5>Prescrição</h5>
                        {medicamentos.map((med, index) => (
                            <div key={med.id} className="medicamento-item">
                                <div className="no-print">
                                    <Row className="mb-2">
                                        <Col md={12}>
                                            <InputGroup>
                                                <InputGroup.Text>{index + 1}.</InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Nome do Medicamento"
                                                    value={med.nome_medicamento}
                                                    onChange={(e) => handleMedicamentoChange(index, 'nome_medicamento', e.target.value)}
                                                />
                                                <Button variant="outline-danger" onClick={() => handleRemoveMedicamento(index)}>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </InputGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Control className="mb-2" type="text" placeholder="Dosagem (ex: 500mg)" value={med.dosagem} onChange={(e) => handleMedicamentoChange(index, 'dosagem', e.target.value)} />
                                            <Form.Control className="mb-2" type="text" placeholder="Via de Administração" value={med.via_administracao} onChange={(e) => handleMedicamentoChange(index, 'via_administracao', e.target.value)} />
                                            <Form.Check type="checkbox" label="Uso Contínuo" checked={med.uso_continuo} onChange={(e) => handleMedicamentoChange(index, 'uso_continuo', e.target.checked)} />
                                        </Col>
                                        <Col md={6}>
                                            <Form.Control className="mb-2" as="textarea" rows={1} placeholder="Posologia (ex: 1 comp. a cada 8h)" value={med.posologia} onChange={(e) => handleMedicamentoChange(index, 'posologia', e.target.value)} />
                                            <Form.Control className="mb-2" type="text" placeholder="Quantidade (ex: 1 caixa)" value={med.quantidade} onChange={(e) => handleMedicamentoChange(index, 'quantidade', e.target.value)} />
                                        </Col>
                                    </Row>
                                    <hr />
                                </div>
                                <div className="print-only medicamento-print-view">
                                    <p><strong>{index + 1}. {med.nome_medicamento} {med.dosagem}</strong></p>
                                    <p><em>{med.via_administracao}</em> - {med.posologia}</p>
                                    <p>Quantidade: {med.quantidade} {med.uso_continuo && <span>- <strong>Uso Contínuo</strong></span>}</p>
                                </div>
                            </div>
                        ))}
                        <div className="no-print">
                            <Button variant="outline-primary" onClick={handleAddMedicamento} className="w-100">
                                <FontAwesomeIcon icon={faPlus} className="me-2" /> Adicionar Medicamento
                            </Button>
                        </div>
                    </div>

                    <div className="receituario-recomendacoes mt-4">
                        <h5>Recomendações Adicionais</h5>
                        <div className="no-print">
                            <Form.Control as="textarea" rows={4} value={recomendacoes} onChange={(e) => { setRecomendacoes(e.target.value); setIsFormDirty(true); }} />
                        </div>
                        <div className="print-only">
                            <p>{recomendacoes || 'Nenhuma recomendação adicional.'}</p>
                        </div>
                    </div>

                    <footer className="receituario-footer">
                        <div className="assinatura-linha">
                            <p>Assinatura do Médico</p>
                        </div>
                    </footer>
                </div>
            </Card.Body>
        </>
    );
};

export default ReceituarioTab;
